import { useState, useEffect, useCallback, useRef } from 'react';
import {
  videoCallService,
  VideoCall,
  CallType,
  CallStatus,
  CallParticipant
} from '../services/videoCallService';
import { webrtcService } from '../services/webrtcService';
import { useWebRTCSignaling } from './useWebRTCSignaling';
import { debugLogger } from '../utils/debugLogger';

export interface UseVideoCallOptions {
  callId?: number;
  roomId?: string;
  conversationId?: number;
  userId: string;
  onCallEnd?: () => void;
  onIncomingCall?: (call: VideoCall) => void;
  onError?: (error: any) => void;
}

export const useVideoCall = (options: UseVideoCallOptions) => {
  const { callId, roomId, conversationId, userId, onCallEnd, onIncomingCall, onError } = options;

  const [call, setCall] = useState<VideoCall | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
  const [participants, setParticipants] = useState<CallParticipant[]>([]);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const callStartTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const screenTrackRef = useRef<MediaStreamTrack | null>(null);
  const originalVideoTrackRef = useRef<MediaStreamTrack | null>(null);

  // WebRTC signaling
  const signaling = useWebRTCSignaling({
    roomId: roomId || callId?.toString() || '',
    userId,
    roomType: 'call',
    onOffer: handleOffer,
    onAnswer: handleAnswer,
    onIceCandidate: handleIceCandidate,
    onUserJoined: handleUserJoined,
    onUserLeft: handleUserLeft,
    onError: (err: any) => {
      debugLogger.log('VideoCall', 'Signaling error', err);
      if (onError) onError(err);
    }
  });

  // Handle incoming offer
  async function handleOffer(senderId: string, offer: RTCSessionDescriptionInit) {
    try {
      const peerConnection = webrtcService.createPeerConnection(
        senderId,
        (candidate) => signaling.sendIceCandidate(senderId, candidate),
        (stream) => {
          setRemoteStreams(prev => new Map(prev).set(senderId, stream));
        }
      );

      await webrtcService.setRemoteDescription(senderId, offer);
      const answer = await webrtcService.createAnswer(senderId);
      signaling.sendAnswer(senderId, answer);
    } catch (error) {
      debugLogger.log('VideoCall', 'Error handling offer', error);
      if (onError) onError(error);
    }
  }

  // Handle incoming answer
  async function handleAnswer(senderId: string, answer: RTCSessionDescriptionInit) {
    try {
      await webrtcService.setRemoteDescription(senderId, answer);
    } catch (error) {
      debugLogger.log('VideoCall', 'Error handling answer', error);
      if (onError) onError(error);
    }
  }

  // Handle incoming ICE candidate
  async function handleIceCandidate(senderId: string, candidate: RTCIceCandidateInit) {
    try {
      await webrtcService.addIceCandidate(senderId, candidate);
    } catch (error) {
      debugLogger.log('VideoCall', 'Error handling ICE candidate', error);
    }
  }

  // Handle user joined
  async function handleUserJoined(joinedUserId: string) {
    debugLogger.log('VideoCall', 'User joined', { joinedUserId });

    try {
      const peerConnection = webrtcService.createPeerConnection(
        joinedUserId,
        (candidate) => signaling.sendIceCandidate(joinedUserId, candidate),
        (stream) => {
          setRemoteStreams(prev => new Map(prev).set(joinedUserId, stream));
        }
      );

      const offer = await webrtcService.createOffer(joinedUserId);
      signaling.sendOffer(joinedUserId, offer);
    } catch (error) {
      debugLogger.log('VideoCall', 'Error creating offer for new participant', error);
      if (onError) onError(error);
    }
  }

  // Handle user left
  function handleUserLeft(leftUserId: string) {
    debugLogger.log('VideoCall', 'User left', { leftUserId });
    
    webrtcService.closePeerConnection(leftUserId);
    setRemoteStreams(prev => {
      const next = new Map(prev);
      next.delete(leftUserId);
      return next;
    });
  }

  // Initiate call
  const initiateCall = useCallback(async (type: CallType = CallType.VIDEO) => {
    if (!conversationId) {
      throw new Error('Conversation ID is required');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create call on server
      const newCall = await videoCallService.initiateCall({
        conversationId,
        type
      });
      setCall(newCall);

      // Get user media
      const mediaStream = await webrtcService.getUserMedia(
        type === CallType.VIDEO,
        true
      );
      setLocalStream(mediaStream);
      setIsVideoEnabled(type === CallType.VIDEO);

      // Join signaling
      signaling.notifyJoin();

      // Start duration timer
      callStartTimeRef.current = Date.now();
      durationIntervalRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
      }, 1000);

      return newCall;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to initiate call';
      setError(errorMsg);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, signaling, onError]);

  // Answer call
  const answerCall = useCallback(async (incomingCallId: number, type: CallType = CallType.VIDEO) => {
    setIsLoading(true);
    setError(null);

    try {
      // Answer call on server
      const answeredCall = await videoCallService.answerCall({ callId: incomingCallId });
      setCall(answeredCall);

      // Get user media
      const mediaStream = await webrtcService.getUserMedia(
        type === CallType.VIDEO,
        true
      );
      setLocalStream(mediaStream);
      setIsVideoEnabled(type === CallType.VIDEO);

      // Join signaling
      signaling.notifyJoin();

      // Start duration timer
      callStartTimeRef.current = Date.now();
      durationIntervalRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
      }, 1000);

      return answeredCall;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to answer call';
      setError(errorMsg);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [signaling, onError]);

  // Reject call
  const rejectCall = useCallback(async (incomingCallId: number, reason?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await videoCallService.rejectCall({ callId: incomingCallId, reason });
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to reject call';
      setError(errorMsg);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [onError]);

  // End call
  const endCall = useCallback(async () => {
    if (!call?.id || !call?.roomId) {
      throw new Error('Call ID and Room ID are required');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Stop screen sharing if active
      if (isSharingScreen) {
        await stopScreenShare();
      }

      // End call on server
      const endedCall = await videoCallService.endCall({ 
        callId: call.id,
        roomId: call.roomId 
      });
      setCall(endedCall);

      // Leave signaling
      signaling.notifyLeave();

      // Cleanup WebRTC
      webrtcService.cleanup();
      setLocalStream(null);
      setRemoteStreams(new Map());

      // Stop duration timer
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      if (onCallEnd) onCallEnd();

      return endedCall;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to end call';
      setError(errorMsg);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [call?.id, isSharingScreen, signaling, onCallEnd, onError]);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    const newState = webrtcService.toggleAudio();
    setIsAudioEnabled(newState);
    return newState;
  }, []);

  // Toggle video
  const toggleVideo = useCallback(() => {
    const newState = webrtcService.toggleVideo();
    setIsVideoEnabled(newState);
    return newState;
  }, []);

  // Start screen share
  const startScreenShare = useCallback(async () => {
    try {
      // Get display media
      const screenStream = await webrtcService.getDisplayMedia();
      const screenTrack = screenStream.getVideoTracks()[0];

      if (!screenTrack) {
        throw new Error('No screen track available');
      }

      // Save original video track
      const localVideoTrack = localStream?.getVideoTracks()[0];
      if (localVideoTrack) {
        originalVideoTrackRef.current = localVideoTrack;
      }

      // Replace video track with screen track
      await webrtcService.replaceVideoTrack(screenTrack);
      screenTrackRef.current = screenTrack;
      setIsSharingScreen(true);

      // Handle screen share stop
      screenTrack.onended = () => {
        stopScreenShare();
      };

      debugLogger.log('VideoCall', 'Started screen sharing');
    } catch (error) {
      debugLogger.log('VideoCall', 'Failed to start screen sharing', error);
      if (onError) onError(error);
      throw error;
    }
  }, [localStream, onError]);

  // Stop screen share
  const stopScreenShare = useCallback(async () => {
    try {
      // Stop screen track
      if (screenTrackRef.current) {
        screenTrackRef.current.stop();
        screenTrackRef.current = null;
      }

      // Restore original video track
      if (originalVideoTrackRef.current) {
        await webrtcService.replaceVideoTrack(originalVideoTrackRef.current);
        originalVideoTrackRef.current = null;
      }

      setIsSharingScreen(false);
      debugLogger.log('VideoCall', 'Stopped screen sharing');
    } catch (error) {
      debugLogger.log('VideoCall', 'Failed to stop screen sharing', error);
      if (onError) onError(error);
    }
  }, [onError]);

  // Format call duration
  const formatDuration = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (screenTrackRef.current) {
        screenTrackRef.current.stop();
      }
      webrtcService.cleanup();
    };
  }, []);

  return {
    call,
    isLoading,
    error,
    localStream,
    remoteStreams,
    participants,
    isAudioEnabled,
    isVideoEnabled,
    isSharingScreen,
    callDuration,
    isConnected: signaling.isConnected,
    initiateCall,
    answerCall,
    rejectCall,
    endCall,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    formatDuration
  };
};
